import { useEffect, useRef } from 'react';
import { Bell, BellOff, X, ArrowRight } from 'lucide-react';
import type { NoticeDto } from '../../../domain/contracts/api.contracts';
import { useTranslation } from '../../../application/hooks/useTranslation';
import { formatDateTime } from '../../../domain/formatters/date';
import { Badge } from '../ui/Badge';
import { Skeleton } from '../ui/Skeleton';
import './NoticesPopover.css';

const CAT_TONE = { CORPORATE: 'info', EMI_REMINDER: 'warning', GENERAL: 'neutral' } as const;
const CAT_KEY = { CORPORATE: 'notices.corporate', EMI_REMINDER: 'notices.emi', GENERAL: 'notices.general' } as const;

export interface NoticesPopoverProps {
  notices: NoticeDto[];
  isLoading?: boolean;
  onClose: () => void;
  onNavigateAll: () => void;
  onSelectNotice?: (notice: NoticeDto) => void;
}

/**
 * Brief view popover for notices (W12 / TopBar).
 * Tapping the notification bell opens this card anchored to the top bar,
 * allowing members to review recent notices or jump directly to the full notice board.
 */
export function NoticesPopover({
  notices,
  isLoading = false,
  onClose,
  onNavigateAll,
  onSelectNotice,
}: NoticesPopoverProps) {
  const { t, locale } = useTranslation();
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Show up to 4 most recent notices in the brief view
  const recentNotices = notices.slice(0, 4);

  return (
    <div
      ref={popoverRef}
      className="notices-popover"
      role="dialog"
      aria-modal="false"
      aria-label={t('notices.recent') ?? 'Recent Notices'}
      data-testid="notices-brief-popover"
    >
      <header className="notices-popover-header">
        <div className="notices-popover-title-row">
          <span className="notices-popover-title-icon" aria-hidden>
            <Bell size={18} />
          </span>
          <h3 className="notices-popover-title">{t('notices.recent') ?? 'Recent Notices'}</h3>
          {notices.length > 0 && <Badge tone="neutral">{notices.length}</Badge>}
        </div>
        <button
          type="button"
          className="notices-popover-close"
          onClick={onClose}
          aria-label={t('common.close')}
          data-testid="notices-popover-close"
        >
          <X size={18} />
        </button>
      </header>

      <div className="notices-popover-content">
        {isLoading ? (
          <div className="notices-popover-loading" data-testid="notices-loading">
            <div className="notices-popover-skeleton-card">
              <Skeleton count={2} height={14} />
            </div>
            <div className="notices-popover-skeleton-card">
              <Skeleton count={2} height={14} />
            </div>
          </div>
        ) : recentNotices.length === 0 ? (
          <div className="notices-popover-empty" data-testid="notices-empty">
            <BellOff size={32} className="notices-popover-empty-icon" aria-hidden />
            <p className="notices-popover-empty-title">{t('notices.empty')}</p>
            <p className="notices-popover-empty-desc">{t('notices.allCaughtUp')}</p>
          </div>
        ) : (
          <ul className="notices-popover-list" role="list">
            {recentNotices.map((n) => (
              <li key={n.id} className="notices-popover-item">
                <button
                  type="button"
                  className="notices-popover-item-btn"
                  onClick={() => {
                    onSelectNotice?.(n);
                    onNavigateAll();
                  }}
                  data-testid="notices-popover-item"
                >
                  <div className="notices-popover-item-header">
                    <Badge tone={CAT_TONE[n.category]}>{t(CAT_KEY[n.category])}</Badge>
                    <span className="notices-popover-date">{formatDateTime(n.publishedAt, locale)}</span>
                  </div>
                  <h4 className="notices-popover-item-title">{n.title}</h4>
                  <p className="notices-popover-item-body">{n.body}</p>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <footer className="notices-popover-footer">
        <button
          type="button"
          className="notices-popover-all-btn"
          onClick={onNavigateAll}
          data-testid="notices-view-all"
        >
          <span>{t('notices.viewAll')}</span>
          <ArrowRight size={16} aria-hidden />
        </button>
      </footer>
    </div>
  );
}
